import { Avatar, Tooltip } from "antd"
import { UserAddOutlined } from "@ant-design/icons"
import { CSSProperties } from "react"
import { CrewMember } from "../scheduleApi"
import { useSchedule } from "../ScheduleContext"
import { getInitials } from "../scheduleModel"
import { findCrewMemberByEmail } from "../crewModel"

type Props = {
  crewForemanEmail: string | null
  crewMemberIds: string | null
  initialsColor?: string
}

const renderCrewAvatar = (member: CrewMember, initialsColor: string) => {
  const useInitials = member.hasStockAvatar || !member.avatar
  const initialsStyle: CSSProperties = {
    backgroundColor: member.color || '#d9d9d9',
    color: initialsColor,
    border: `1px solid ${initialsColor}`,
    fontSize: '10px',
  }
  return (
    <Avatar
      size={20}
      gap={2}
      src={useInitials ? undefined : member.avatar}
      style={useInitials ? initialsStyle : undefined}
    >
      {getInitials(member.name)}
    </Avatar>
  )
}

const AvatarGroup = ({ crewForemanEmail, crewMemberIds, initialsColor = '#fff' }: Props) => {
  const { crew, isCrewLoading } = useSchedule()

  const crewLead = !isCrewLoading && crewForemanEmail
    ? findCrewMemberByEmail(crew, crewForemanEmail)
    : null
  const hasValidCrewLead = !!crewLead

  const crewMembersFromIds = !isCrewLoading && crewMemberIds
    ? crewMemberIds.split(',')
      .map(id => crew?.find(c => c.id === parseInt(id)))
      .filter((member): member is CrewMember => member !== undefined)
    : []

  const crewLeadNotInMembers = crewLead && !crewMembersFromIds.some(m => m.id === crewLead.id)
  const validCrewMembers = crewLeadNotInMembers
    ? [crewLead, ...crewMembersFromIds]
    : crewMembersFromIds

  if (validCrewMembers.length > 0) {
    const sortedMembers = [...validCrewMembers].sort((a, b) => {
      if (a.id === crewLead?.id) return -1
      if (b.id === crewLead?.id) return 1
      return 0
    })

    const displayMember = hasValidCrewLead ? sortedMembers[0] : null
    const overflowCount = hasValidCrewLead ? sortedMembers.length - 1 : sortedMembers.length

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {!hasValidCrewLead && (
          <Tooltip title={"No Crew Lead"}>
            <div style={{ position: 'relative', zIndex: 1, display: 'flex' }}>
              <Avatar size="small" style={{ backgroundColor: '#FFF1F0', border: '1px dashed #820014', color: '#820014' }} icon={<UserAddOutlined />} />
            </div>
          </Tooltip>
        )}
        {displayMember && (
          <div style={{ position: 'relative', zIndex: 1, display: 'flex' }}>
            {renderCrewAvatar(displayMember, initialsColor)}
          </div>
        )}
        {overflowCount > 0 && (
          <Avatar
            size={18}
            style={{
              backgroundColor: '#f0f0f0',
              color: '#999',
              fontSize: '9px',
              marginLeft: '-8px',
              position: 'relative',
              zIndex: 0,
            }}
          >
            +{overflowCount}
          </Avatar>
        )}
      </div>
    )
  }

  if (hasValidCrewLead) {
    return <Tooltip title={crewLead.name}>
      {renderCrewAvatar(crewLead, initialsColor)}
    </Tooltip>
  }

  return <></>
}

export default AvatarGroup